import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { getProductLines } from '../../data/productsLoader';

const orderMap: Record<string, number> = {
  zwolf: 1,
  zcougar: 2,
  zrino: 3,
  zlion: 4,
  zfox: 5,
  ztoy: 6
};

const homeRangeCopy: Record<string, { headline: string; description: string; highlights: string[] }> = {
  zwolf: {
    headline: 'Todo terreno con potencia',
    description: 'Para rutas exigentes, mayor tracción y suspensión preparada para superficies difíciles.',
    highlights: ['Hasta 70 km', 'Suspensión hidráulica']
  },
  zcougar: {
    headline: 'Confort para largas rutas',
    description: 'Autonomía amplia y suspensión DUO-SHOCK para moverte con estabilidad cada día.',
    highlights: ['Hasta 70 km', 'DUO-SHOCK']
  },
  zrino: {
    headline: 'Rendimiento de largo alcance',
    description: 'Potencia sólida para recorridos intensos con control y estabilidad.',
    highlights: ['Doble motor', 'Alto rendimiento']
  },
  zlion: {
    headline: 'Equilibrio urbano pro',
    description: 'Una opción urbana completa para moverte a diario con comodidad y autonomía.',
    highlights: ['Ciudad y distancia', 'Confort diario']
  },
  zfox: {
    headline: 'Ligero para la ciudad',
    description: 'Diseño plegable y ágil para trayectos urbanos cortos sin complicaciones.',
    highlights: ['Plegable', 'Uso urbano']
  },
  ztoy: {
    headline: 'Primeras aventuras',
    description: 'Patinete infantil ligero y seguro para disfrutar al aire libre.',
    highlights: ['Desde 6 años', 'Manejo sencillo']
  }
};

function RangePill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-black/[0.06] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-700">
      {children}
    </span>
  );
}

export function ProductRangesPreview() {
  const productLines = [...getProductLines()]
    .sort((a, b) => (orderMap[a.id] || 999) - (orderMap[b.id] || 999))
    .slice(0, 6);

  return (
    <section id="products" className="py-24 md:py-28 bg-[linear-gradient(180deg,var(--zw-ivory)_0%,#fff_100%)] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-[6%] h-64 w-64 rounded-full bg-[#ece1cf]/55 blur-3xl" />
        <div className="absolute bottom-6 left-[8%] h-56 w-56 rounded-full bg-[#dfe8da]/50 blur-3xl" />
      </div>

      <div className="home-shell">
        <div className="grid grid-cols-1 xl:grid-cols-[0.8fr_1.2fr] gap-8 items-end mb-12">
          <div className="max-w-2xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="home-kicker"
            >
              Gamas principales
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 }}
              className="home-title mb-5"
            >
              Una gama para cada forma de moverte
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="home-copy"
            >
              Seis gamas Zwheel para ciudad, largo alcance, off-road e infantiles. Encuentra rápido la que encaja con tu uso.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="xl:justify-self-end"
          >
            <Button asChild variant="outline" className="rounded-full px-6 bg-white">
              <Link to="/patinetes-electricos">
                Ver todas las gamas
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {productLines.map((range, index) => {
            const preview = homeRangeCopy[range.id] ?? {
              headline: range.tagline,
              description: range.description,
              highlights: range.features.slice(0, 2)
            };

            return (
              <motion.article
                key={range.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="group flex h-full flex-col overflow-hidden rounded-[2rem] border border-black/[0.06] bg-white shadow-[0_25px_70px_rgba(25,22,18,0.08)]"
              >
                <div className="relative aspect-square overflow-hidden bg-gray-100">
                  <img
                    src={range.coverImage || '/placeholder-scooter.png'}
                    alt={range.name}
                    className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/placeholder-scooter.png';
                    }}
                  />
                </div>

                <div className="flex flex-1 flex-col p-6 md:p-7">
                  <div className="flex flex-wrap gap-2 mb-4">
                    <RangePill>Serie {range.name}</RangePill>
                    <RangePill>{range.products.length} modelo{range.products.length === 1 ? '' : 's'}</RangePill>
                  </div>

                  <div className="flex flex-1 flex-col">
                    <h3 className="text-2xl font-bold tracking-tight text-gray-950 md:text-[1.7rem]">
                      {preview.headline}
                    </h3>
                    <p className="mt-3 min-h-[3rem] text-sm leading-6 text-gray-600 line-clamp-2">
                      {preview.description}
                    </p>

                    <div className="mt-5 grid grid-cols-2 gap-2">
                      {preview.highlights.map((feature) => (
                        <div key={feature} className="rounded-2xl bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-800">
                          {feature}
                        </div>
                      ))}
                    </div>

                    <Button asChild className="mt-auto w-fit rounded-full bg-gray-950 px-6 text-white hover:bg-gray-800">
                      <Link to={`/patinetes-electricos#${range.id}`}>
                        Ver gama
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
